// TaskDataEditModal - Modal for editing individual task values in data groups
// Uses TaskInputRenderer for consistent input rendering

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Box,
  Icon,
  Link,
  useColorModeValue,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { MdSave, MdLock, MdLocationOn, MdOpenInNew, MdHistory } from 'react-icons/md';
import { Modal } from '@/client/components/shared/Modal';
import type { TaskData } from '@/lib/sdk/chatbot/types';
import { TaskType } from '@/lib/sdk/chatbot/types';
import type { ChoiceOption } from '@/lib/sdk/chatbot/types';
import type { MediaPayload } from '@/lib/sdk/workflows/types';
import { TaskInputRenderer } from '@/client/components/workflows/TaskInputRenderer';
import { adaptTaskDataForInput, hasMediaAttachments } from '@/client/utils/taskDataAdapter';
import { isHiddenTaskType } from '@/client/utils/taskTypeMapping';
import { MediaDisplay } from './MediaDisplay';

interface TaskDataEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskData | null;
  botCode: string;
  onSubmit: (taskId: string, noteId: string, value: string, repId: number) => Promise<void>;
  isLoading: boolean;
  canEdit: boolean;
}

/**
 * Modal for editing a single task's value in data groups
 * Reuses TaskInputRenderer for consistent input components
 */
export function TaskDataEditModal({
  isOpen,
  onClose,
  task,
  botCode,
  onSubmit,
  isLoading,
  canEdit,
}: TaskDataEditModalProps) {
  const [textValue, setTextValue] = useState('');
  const [selectedChoice, setSelectedChoice] = useState<ChoiceOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  const previousTaskId = useRef<number | null>(null);

  const warningBg = useColorModeValue('orange.50', 'orange.900');

  // Reset state when task changes
  useEffect(() => {
    if (task && task.id !== previousTaskId.current) {
      previousTaskId.current = task.id;
      setTextValue(task.note || '');
      setSelectedChoice(null);
      setError(null);
    }
  }, [task]);

  // Handle text value changes
  const handleTextValue = useCallback((value: string) => {
    setTextValue(value);
    setError(null);
  }, []);

  // Handle choice changes
  const handleChoiceValue = useCallback((choice: ChoiceOption | null) => {
    setSelectedChoice(choice);
    if (choice) {
      setTextValue(choice.text);
    }
    setError(null);
  }, []);

  // Handle media changes (not used for editing, but required by TaskInputRenderer)
  const handleMediaValue = useCallback((_media: MediaPayload | null, _isUploading: boolean) => {
    // Media upload not supported in data editing mode
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!task) return;

    // Validate required fields
    if (!textValue.trim() && task.type === TaskType.TEXT) {
      setError('Please enter a value');
      return;
    }

    setError(null);

    try {
      await onSubmit(task.task_id, task.note_id, textValue, task.id);
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save changes';
      setError(errorMessage);
    }
  }, [task, textValue, onSubmit, onClose]);

  // Handle modal close
  const handleClose = useCallback(() => {
    setTextValue('');
    setSelectedChoice(null);
    setError(null);
    onClose();
  }, [onClose]);

  if (!task) return null;

  // Check if task is editable
  const isEditable = canEdit && task.mutable;
  const hasMedia = hasMediaAttachments(task);

  // Adapt TaskData to WebTaskItem for TaskInputRenderer
  const webTaskItem = adaptTaskDataForInput(task as any, botCode);

  // Don't show hidden task types
  if (isHiddenTaskType(task.type)) {
    return null;
  }

  const footer = (
    <HStack spacing={3} w="full" justify="flex-end">
      <Button variant="ghost" onClick={handleClose} isDisabled={isLoading}>
        Cancel
      </Button>
      <Button
        colorScheme="brand"
        leftIcon={isEditable ? <MdSave /> : <MdLock />}
        onClick={handleSubmit}
        isLoading={isLoading}
        loadingText="Saving..."
        isDisabled={!isEditable || isLoading}
      >
        {isEditable ? 'Save Changes' : 'Read Only'}
      </Button>
    </HStack>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={task.name}
      footer={footer}
      size="md"
    >
      <VStack spacing={4} align="stretch">
        {/* Task type and status badges */}
        <HStack flexWrap="wrap" gap={2}>
          <Badge colorScheme="purple" fontSize="xs">
            {task.type}
          </Badge>
          {!task.mutable && (
            <Badge colorScheme="red" fontSize="xs">
              Read Only
            </Badge>
          )}
          {task.Is_edited && (
            <Badge colorScheme="yellow" fontSize="xs">
              Edited
            </Badge>
          )}
        </HStack>

        {/* Task description if available */}
        {task.description && (
          <Text fontSize="sm" color="text-secondary">
            {task.description}
          </Text>
        )}

        {/* Non-editable warning */}
        {!isEditable && (
          <Box p={3} bg={warningBg} borderRadius="md">
            <HStack>
              <Icon as={MdLock} color="orange.500" />
              <Text fontSize="sm" color="orange.700">
                {!canEdit
                  ? 'You do not have permission to edit this task.'
                  : 'This task is marked as read-only and cannot be modified.'}
              </Text>
            </HStack>
          </Box>
        )}

        {/* Location display if available */}
        {task.location?.latitude && task.location?.longitude && (
          <Box>
            <HStack spacing={1} mb={1}>
              <Icon as={MdLocationOn} color="text-muted" boxSize={4} />
              <Text fontSize="sm" fontWeight="medium" color="text-secondary">
                Location
              </Text>
            </HStack>
            <Link
              href={`https://www.google.com/maps?q=${task.location.latitude},${task.location.longitude}`}
              isExternal
              fontSize="sm"
              color="brand.500"
            >
              {task.location.latitude.toFixed(6)}, {task.location.longitude.toFixed(6)}
              <Icon as={MdOpenInNew} ml={1} boxSize={3} />
            </Link>
          </Box>
        )}

        {/* Media attachments (read-only) */}
        {hasMedia && (
          <Box>
            <Text fontSize="sm" fontWeight="medium" color="text-secondary" mb={2}>
              Attachments
            </Text>
            <MediaDisplay
              photos={task.photos}
              videos={task.videos}
              documents={task.documents}
              media={task.media}
            />
          </Box>
        )}

        {/* Edit History / Audit Trail */}
        {task.edit_history && task.edit_history.length > 0 && (
          <>
            <Divider />
            <Accordion allowToggle>
              <AccordionItem border="none">
                <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                  <HStack flex="1">
                    <Icon as={MdHistory} color="text-muted" />
                    <Text fontSize="sm" fontWeight="medium" color="text-secondary">
                      Edit History ({task.edit_history.length})
                    </Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={0} pb={0}>
                  <VStack spacing={2} align="stretch">
                    {task.edit_history.map((entry, index) => (
                      <Box
                        key={entry.id || index}
                        p={2}
                        bg="bg-muted"
                        borderRadius="md"
                        borderLeftWidth="3px"
                        borderLeftColor="orange.400"
                      >
                        <Text fontSize="xs" color="text-muted" mb={1}>
                          {entry.name && `${entry.name} • `}
                          {new Date(entry.updated_at).toLocaleString()}
                        </Text>
                        {entry.audit_metadata ? (
                          <Text fontSize="sm" color="text-primary">
                            Changed from &quot;{entry.audit_metadata.old_value}&quot; to &quot;{entry.audit_metadata.new_value}&quot;
                          </Text>
                        ) : (
                          <Text fontSize="sm" color="text-primary">
                            {entry.status_text}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </>
        )}

        {/* Input field rendered by TaskInputRenderer */}
        {isEditable && (
          <Box>
            <Text fontSize="sm" fontWeight="medium" color="text-secondary" mb={2}>
              Value
            </Text>
            <TaskInputRenderer
              task={webTaskItem}
              onTextValue={handleTextValue}
              onMediaValue={handleMediaValue}
              onChoiceValue={handleChoiceValue}
              initialTextValue={task.note || ''}
              initialChoice={selectedChoice || undefined}
            />
          </Box>
        )}

        {/* Current value display for read-only tasks */}
        {!isEditable && (
          <Box>
            <Text fontSize="sm" fontWeight="medium" color="text-secondary" mb={1}>
              Current Value
            </Text>
            <Text
              fontSize="md"
              p={3}
              bg="bg-muted"
              borderRadius="md"
              borderWidth="1px"
              borderColor="border-default"
              color="text-primary"
            >
              {task.note || 'No value'}
            </Text>
          </Box>
        )}

        {/* Error display */}
        {error && (
          <Text fontSize="sm" color="red.500">
            {error}
          </Text>
        )}
      </VStack>
    </Modal>
  );
}
